/**
 * Accountant Dashboard — Financial Operations Center.
 *
 * 8 tabs:
 *  1. Overview     — KPIs + Net P&L + Cash balance + recent activity
 *  2. Income       — Approved payments timeline + by-method breakdown
 *  3. Expenses     — Excel-like grid with quick-add + approve workflow
 *  4. Salaries     — Monthly payroll with auto-bonus + Pay action
 *  5. Net Profit   — Trend over months
 *  6. Cash Flow    — Drawer balance + daily ins/outs
 *  7. Ledger       — Append-only journal with integrity check
 *  8. Period Lock  — STAS-only month closing
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, RotateCw, TrendingUp, TrendingDown, Wallet,
  Banknote, Receipt, Users, Lock, Unlock, ShieldCheck, Plus,
  CheckCircle2, XCircle, Trash2, AlertTriangle, HandCoins, Handshake,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtMoney, fmtNum, fmtDate, fmtDateTime } from "@/lib/format";
import Money from "@/components/Money";
import RiyalSymbol from "@/components/RiyalSymbol";

const TABS = [
  { id: "overview", icon: TrendingUp, label: "نظرة عامة" },
  { id: "income",   icon: Banknote,   label: "الدخل" },
  { id: "expenses", icon: Receipt,    label: "المصاريف" },
  { id: "salaries", icon: Users,      label: "الرواتب" },
  { id: "partners", icon: HandCoins,  label: "الشركاء (دائن/مدين)" },
  { id: "partnership", icon: Handshake, label: "إعدادات الشراكة" },
  { id: "profit",   icon: TrendingUp, label: "صافي الربح" },
  { id: "cash",     icon: Wallet,     label: "Cash Flow" },
  { id: "ledger",   icon: ShieldCheck, label: "دفتر القيود" },
  { id: "locks",    icon: Lock,       label: "قفل الفترة" },
];

const now = new Date();
const PERIOD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

export default function FinancePage() {
  const { user } = useAuth();
  const { theme, dir } = useI18n();
  const navigate = useNavigate();
  const isRoot = !!user?.role?.is_root;
  const roleCode = user?.role?.code;
  const canApprove = isRoot || roleCode === "general_manager" || roleCode === "deputy_manager";
  const canRead = canApprove || roleCode === "accountant";

  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState(PERIOD);
  const css = makeStyles(theme);
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  if (!user) return null;
  if (!canRead) {
    return (
      <div dir={dir} style={css.page}>
        <div style={css.err}>هذه الصفحة متاحة للمحاسب والمدراء فقط.</div>
      </div>
    );
  }

  const [y, m] = period.split("-").map((s) => parseInt(s, 10));

  return (
    <div dir={dir} style={css.page} data-testid="finance-page">
      <header style={css.head}>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="finance-back">
          <BackIcon size={14} strokeWidth={2.4} /> رجوع
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={css.title}>لوحة المحاسب — النظام المالي</h1>
          <p style={css.sub}>كل القيود والمصاريف والرواتب مرتبطة بالـ Ledger الموثّق</p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} theme={theme} />
      </header>

      <nav style={css.tabsBar}>
        {TABS.map((t) => {
          if (t.id === "locks" && !isRoot) return null;
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`finance-tab-${t.id}`}
              style={{ ...css.tab, ...(active ? css.tabActive : {}) }}
            >
              <Icon size={14} strokeWidth={2.4} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </nav>

      <main style={css.body}>
        {tab === "overview" && <OverviewTab year={y} month={m} theme={theme} css={css} />}
        {tab === "income" && <IncomeTab year={y} month={m} theme={theme} css={css} />}
        {tab === "expenses" && <ExpensesTab year={y} month={m} theme={theme} css={css} canApprove={canApprove} />}
        {tab === "salaries" && <SalariesTab period={period} theme={theme} css={css} canApprove={canApprove} />}
        {tab === "partners" && <PartnersTab theme={theme} css={css} canWrite={canRead && (isRoot || roleCode === "accountant")} />}
        {tab === "partnership" && <PartnershipTab theme={theme} css={css} isRoot={isRoot} canWrite={canRead && (isRoot || roleCode === "accountant")} />}
        {tab === "profit" && <ProfitTab theme={theme} css={css} />}
        {tab === "cash" && <CashTab theme={theme} css={css} />}
        {tab === "ledger" && <LedgerTab year={y} month={m} theme={theme} css={css} />}
        {tab === "locks" && isRoot && <LocksTab theme={theme} css={css} />}
      </main>
    </div>
  );
}

/* ─────────────────────── Period Picker ─────────────────────── */
function PeriodPicker({ value, onChange, theme }) {
  const [y, m] = value.split("-").map((s) => parseInt(s, 10));
  return (
    <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <select value={m} onChange={(e) => onChange(`${y}-${String(e.target.value).padStart(2, "0")}`)}
        style={inputSt(theme)} data-testid="finance-month">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((mm) => (
          <option key={mm} value={mm}>{String(mm).padStart(2, "0")}</option>
        ))}
      </select>
      <select value={y} onChange={(e) => onChange(`${e.target.value}-${String(m).padStart(2, "0")}`)}
        style={inputSt(theme)} data-testid="finance-year">
        {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i).map((yy) => (
          <option key={yy} value={yy}>{yy}</option>
        ))}
      </select>
    </div>
  );
}

/* ─────────────────────── Tab: Overview ─────────────────────── */
function OverviewTab({ year, month, theme, css }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => {
    try { setErr(""); const r = await api.get(`/finance/dashboard?year=${year}&month=${month}`); setData(r.data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);
  if (err) return <div style={css.err}>{err}</div>;
  if (!data) return <div style={css.muted}>…</div>;

  return (
    <div>
      <div style={css.kpiGrid}>
        <KPI icon={Banknote} label="إجمالي الدخل" value={data.income?.total || 0} theme={theme} testid="kpi-income" color="#16a34a" />
        <KPI icon={Receipt}  label="إجمالي المصاريف" value={data.expenses?.total || 0} theme={theme} testid="kpi-expenses" color="#dc2626" />
        <KPI icon={Users}    label="الرواتب المصروفة" value={data.salaries?.total || 0} theme={theme} testid="kpi-salaries" color="#a855f7" />
        <KPI icon={TrendingUp} label="صافي الربح" value={data.net_profit || 0} theme={theme} testid="kpi-net" color={data.net_profit >= 0 ? "#16a34a" : "#dc2626"} bold />
        <KPI icon={Wallet}   label="رصيد الصندوق" value={data.cash_balance || 0} theme={theme} testid="kpi-cash" color={theme.gold} />
        <div style={{ ...css.kpiCard, justifyContent: "center" }} data-testid="kpi-lock">
          <div style={css.kpiLabel}>حالة الشهر</div>
          <div style={{ ...css.kpiValue, color: data.period_locked ? "#dc2626" : "#16a34a", fontSize: 16 }}>
            {data.period_locked ? "🔒 مقفل" : "🔓 مفتوح"}
          </div>
        </div>
      </div>

      <section style={css.card}>
        <h2 style={css.cardTitle}>الدخل بحسب طريقة الدفع</h2>
        {(data.income_by_method || []).length === 0
          ? <div style={css.muted}>لا توجد مدفوعات في هذا الشهر بعد.</div>
          : <Table head={["الطريقة", "العدد", "الإجمالي"]} rows={(data.income_by_method || []).map((r) => [
              methodLabel(r.method), fmtNum(r.count), <Money amount={r.total} size={12} bold />,
            ])} theme={theme} testid="overview-income-by-method" />}
      </section>

      <section style={css.card}>
        <h2 style={css.cardTitle}>المصاريف بحسب الفئة</h2>
        {(data.expenses_by_category || []).length === 0
          ? <div style={css.muted}>لا مصاريف مسجّلة هذا الشهر.</div>
          : <Table head={["الفئة", "العدد", "الإجمالي"]} rows={(data.expenses_by_category || []).map((r) => [
              r.category, fmtNum(r.count), <Money amount={r.total} size={12} bold />,
            ])} theme={theme} testid="overview-expenses-by-cat" />}
      </section>
    </div>
  );
}

function KPI({ icon: Icon, label, value, theme, testid, color, bold }) {
  return (
    <div style={{ ...kpiCardSt(theme), borderColor: color + "55" }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color }}>
        <Icon size={16} strokeWidth={2.4} />
        <span style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700 }}>{label}</span>
      </div>
      <div style={{ marginTop: 8, fontSize: 22, color: color || theme.text, fontWeight: bold ? 900 : 800 }}>
        <Money amount={value} size={16} bold color={color} />
      </div>
    </div>
  );
}

/* ─────────────────────── Tab: Income (ledger filtered) ─────────────────────── */
function IncomeTab({ year, month, theme, css }) {
  const { user } = useAuth();
  const isRoot = !!user?.role?.is_root;
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const load = () => {
    api.get(`/finance/ledger?type=income&year=${year}&month=${month}&limit=200`)
      .then((r) => setItems(r.data.items || [])).catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  // STAS-only: reverse a single income entry. Uses the existing bulk-void
  // endpoint with a one-item payload so the same hash-chain rules apply.
  const forceVoid = async (id, amount, appNum) => {
    const reason = window.prompt(
      `إبطال قيد دخل (${appNum || "بدون رقم"} — ${fmtMoney(amount)} ر.س)\n\n` +
      `سيتم إنشاء قيد عكسي + خصم تلقائي من أرباح الموظف.\n\nاكتب السبب:`,
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post("/finance/ledger/bulk-void", { entry_ids: [id], reason: reason.trim() });
      load();
      alert("✓ تم إبطال القيد وعكسه في الدفتر.");
    } catch (e) { setErr(formatApiError(e)); }
  };

  if (err) return <div style={css.err}>{err}</div>;
  return (
    <section style={css.card}>
      <h2 style={css.cardTitle}>سجل الدخل المعتمد ({items.length} قيد)</h2>
      {items.length === 0 ? <div style={css.muted}>لا توجد قيود دخل بعد.</div> : (
        <Table head={["التاريخ", "المعاملة", "الوصف", "الطريقة", "المبلغ", "الحالة", isRoot ? "إجراءات" : ""].filter(Boolean)}
          rows={items.map((it) => {
            const row = [
              fmtDate(it.date), it.metadata?.application_number || "—",
              truncate(it.description, 50), methodLabel(it.category),
              <Money amount={it.amount} size={12} bold color={it.voided ? "#94a3b8" : "#16a34a"} />,
              <StatusPill status={it.voided ? "voided" : "approved"} />,
            ];
            if (isRoot) {
              row.push(
                !it.voided ? (
                  <IconBtn onClick={() => forceVoid(it.id, it.amount, it.metadata?.application_number)} title="إبطال (STAS)" color="#dc2626" testid={`force-void-income-${it.id}`}>
                    <Trash2 size={14} />
                  </IconBtn>
                ) : <span style={{ fontSize: 10, opacity: 0.6 }}>—</span>,
              );
            }
            return row;
          })} theme={theme} testid="income-table" />)}
    </section>
  );
}

/* ─────────────────────── Tab: Expenses ─────────────────────── */
function ExpensesTab({ year, month, theme, css, canApprove }) {
  const { user } = useAuth();
  const isRoot = !!user?.role?.is_root;
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ category: "", description: "", amount: "", payment_method: "cash", vendor: "", reference_number: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setErr(""); const r = await api.get(`/finance/expenses?year=${year}&month=${month}&limit=200`); setItems(r.data.items || []); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.category || !form.description || !form.amount) return;
    setBusy(true);
    try {
      await api.post("/finance/expenses", { ...form, amount: parseFloat(form.amount) });
      setForm({ category: "", description: "", amount: "", payment_method: "cash", vendor: "", reference_number: "" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const approve = async (id) => {
    setBusy(true);
    try { await api.post(`/finance/expenses/${id}/approve`, { note: "" }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const reject = async (id) => {
    const note = window.prompt("سبب الرفض:");
    if (note === null) return;
    setBusy(true);
    try { await api.post(`/finance/expenses/${id}/reject`, { note }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("حذف المسودة؟")) return;
    setBusy(true);
    try { await api.delete(`/finance/expenses/${id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  // STAS Force-Void: reverses an approved expense by appending a reversal
  // ledger entry and flagging both docs as voided. The hash chain is
  // preserved so finance audit reports stay valid.
  const forceVoid = async (id, amount, category) => {
    const reason = window.prompt(
      `إبطال مصروف معتمد (${category} — ${fmtMoney(amount)} ر.س)\n\n` +
      `سيتم إنشاء قيد عكسي تلقائياً في الدفتر.\n\nاكتب سبب الإبطال:`,
    );
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/finance/expenses/${id}/force-void`, { reason: reason.trim() });
      await load();
      alert("✓ تم إبطال المصروف وإنشاء قيد عكسي.");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section style={css.card}>
        <h2 style={css.cardTitle}>إضافة مصروف جديد</h2>
        <form onSubmit={submit} style={css.formRow}>
          <input placeholder="الفئة (مثال: rent / utilities)" value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputSt(theme)} data-testid="exp-category" />
          <input placeholder="الوصف" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...inputSt(theme), flex: "2 1 200px" }} data-testid="exp-description" />
          <input type="number" placeholder="المبلغ" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ ...inputSt(theme), maxWidth: 120 }} data-testid="exp-amount" />
          <select value={form.payment_method}
            onChange={(e) => setForm({ ...form, payment_method: e.target.value })} style={inputSt(theme)} data-testid="exp-method">
            <option value="cash">كاش</option>
            <option value="mada">مدى</option>
            <option value="visa_network">فيزا</option>
            <option value="bank_transfer">تحويل بنكي</option>
          </select>
          <input placeholder="المورّد (اختياري)" value={form.vendor}
            onChange={(e) => setForm({ ...form, vendor: e.target.value })} style={inputSt(theme)} data-testid="exp-vendor" />
          <input placeholder="رقم مرجعي (اختياري)" value={form.reference_number}
            onChange={(e) => setForm({ ...form, reference_number: e.target.value })} style={inputSt(theme)} data-testid="exp-ref" />
          <button type="submit" disabled={busy} style={btnGold(theme)} data-testid="exp-add">
            <Plus size={14} strokeWidth={2.4} /> إضافة
          </button>
        </form>
      </section>

      {err && <div style={css.err}>{err}</div>}

      <section style={css.card}>
        <h2 style={css.cardTitle}>المصاريف ({items.length})</h2>
        {items.length === 0 ? <div style={css.muted}>لا توجد مصاريف مسجّلة بعد.</div> : (
          <Table head={["التاريخ", "الفئة", "الوصف", "الطريقة", "المبلغ", "الحالة", "إجراءات"]}
            rows={items.map((it) => [
              fmtDate(it.expense_date), it.category, truncate(it.description, 40),
              methodLabel(it.payment_method),
              <Money amount={it.amount} size={12} bold />,
              <StatusPill status={it.voided ? "voided" : it.status} />,
              <div style={{ display: "flex", gap: 4 }}>
                {it.status === "draft" && canApprove && (<>
                  <IconBtn onClick={() => approve(it.id)} title="اعتماد" color="#16a34a" testid={`approve-${it.id}`}><CheckCircle2 size={14} /></IconBtn>
                  <IconBtn onClick={() => reject(it.id)} title="رفض" color="#dc2626" testid={`reject-${it.id}`}><XCircle size={14} /></IconBtn>
                </>)}
                {it.status === "draft" && <IconBtn onClick={() => remove(it.id)} title="حذف" color="#64748b" testid={`del-${it.id}`}><Trash2 size={14} /></IconBtn>}
                {/* STAS-only: force-void an approved expense (reverses the ledger). */}
                {it.status === "approved" && !it.voided && isRoot && (
                  <IconBtn onClick={() => forceVoid(it.id, it.amount, it.category)} title="إبطال (STAS)" color="#dc2626" testid={`force-void-exp-${it.id}`}>
                    <Trash2 size={14} />
                  </IconBtn>
                )}
              </div>,
            ])} theme={theme} testid="expenses-table" />
        )}
      </section>
    </div>
  );
}

/* ─────────────────────── Tab: Salaries ─────────────────────── */
function SalariesTab({ period, theme, css, canApprove }) {
  const { user } = useAuth();
  const isRoot = !!user?.role?.is_root;
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ user_id: "", base_salary: "", deductions: "0", payment_method: "bank_transfer" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setErr("");
      const [s, e] = await Promise.all([
        api.get(`/finance/salaries?period=${period}`),
        api.get(`/users/assignable`),
      ]);
      setItems(s.data.items || []); setEmployees(e.data || []);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.user_id || !form.base_salary) return;
    setBusy(true);
    try {
      await api.post("/finance/salaries", {
        user_id: form.user_id, period,
        base_salary: parseFloat(form.base_salary),
        deductions: parseFloat(form.deductions || "0"),
        payment_method: form.payment_method,
      });
      setForm({ user_id: "", base_salary: "", deductions: "0", payment_method: "bank_transfer" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const pay = async (id, method) => {
    const ref = method !== "cash" ? window.prompt("رقم مرجعي (اختياري):") || "" : "";
    setBusy(true);
    try { await api.post(`/finance/salaries/${id}/pay`, { payment_method: method, reference_number: ref }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("حذف مسودة الراتب؟")) return;
    setBusy(true);
    try { await api.delete(`/finance/salaries/${id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  // STAS-only: reverse a paid salary. Inserts a reversal ledger entry and
  // marks both the salary doc and the original ledger row as voided.
  const forceVoid = async (id, net, name) => {
    const reason = window.prompt(
      `إبطال راتب مصروف للموظف "${name}" بقيمة ${fmtMoney(net)} ر.س\n\n` +
      `سيتم إنشاء قيد عكسي تلقائياً.\n\nاكتب سبب الإبطال:`,
    );
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/finance/salaries/${id}/force-void`, { reason: reason.trim() });
      await load();
      alert("✓ تم إبطال الراتب وإنشاء قيد عكسي.");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section style={css.card}>
        <h2 style={css.cardTitle}>تجهيز راتب — {period}</h2>
        <form onSubmit={submit} style={css.formRow}>
          <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
            style={{ ...inputSt(theme), flex: "2 1 200px" }} data-testid="sal-user">
            <option value="">— اختر موظفاً —</option>
            {employees.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <input type="number" placeholder="الراتب الأساسي" value={form.base_salary}
            onChange={(e) => setForm({ ...form, base_salary: e.target.value })} style={{ ...inputSt(theme), maxWidth: 140 }} data-testid="sal-base" />
          <input type="number" placeholder="الخصومات" value={form.deductions}
            onChange={(e) => setForm({ ...form, deductions: e.target.value })} style={{ ...inputSt(theme), maxWidth: 120 }} data-testid="sal-ded" />
          <select value={form.payment_method}
            onChange={(e) => setForm({ ...form, payment_method: e.target.value })} style={inputSt(theme)} data-testid="sal-method">
            <option value="bank_transfer">تحويل بنكي</option>
            <option value="cash">كاش</option>
          </select>
          <button type="submit" disabled={busy} style={btnGold(theme)} data-testid="sal-add">
            <Plus size={14} strokeWidth={2.4} /> تجهيز
          </button>
        </form>
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>
          💡 البونص يُحتسب تلقائياً من نشاط الموظف في {period} (سياسة 15K → 15% + 500 عند 20K).
        </div>
      </section>

      {err && <div style={css.err}>{err}</div>}

      <section style={css.card}>
        <h2 style={css.cardTitle}>الرواتب ({items.length})</h2>
        {items.length === 0 ? <div style={css.muted}>لا توجد رواتب مسجّلة لهذا الشهر.</div> : (
          <Table head={["الموظف", "الأساسي", "البونص", "الخصومات", "الصافي", "الحالة", "إجراءات"]}
            rows={items.map((it) => [
              it.user?.full_name || it.user_full_name || "—",
              <Money amount={it.base_salary} size={12} />, <Money amount={it.bonus_amount} size={12} color="#16a34a" />,
              <Money amount={it.deductions} size={12} color="#dc2626" />,
              <Money amount={it.net_amount} size={13} bold color={theme.gold} />,
              <StatusPill status={it.voided ? "voided" : it.status} />,
              <div style={{ display: "flex", gap: 4 }}>
                {it.status === "draft" && canApprove && (<>
                  <IconBtn onClick={() => pay(it.id, "bank_transfer")} title="صرف بنكي" color="#16a34a" testid={`pay-bank-${it.id}`}><CheckCircle2 size={14} /></IconBtn>
                  <IconBtn onClick={() => pay(it.id, "cash")} title="صرف نقدي" color={theme.gold} testid={`pay-cash-${it.id}`}><Banknote size={14} /></IconBtn>
                </>)}
                {it.status === "draft" && <IconBtn onClick={() => remove(it.id)} title="حذف" color="#64748b" testid={`del-sal-${it.id}`}><Trash2 size={14} /></IconBtn>}
                {/* STAS-only force-void for paid salaries */}
                {it.status === "paid" && !it.voided && isRoot && (
                  <IconBtn onClick={() => forceVoid(it.id, it.net_amount, it.user?.full_name || it.user_full_name)} title="إبطال (STAS)" color="#dc2626" testid={`force-void-sal-${it.id}`}>
                    <Trash2 size={14} />
                  </IconBtn>
                )}
              </div>,
            ])} theme={theme} testid="salaries-table" />)}
      </section>
    </div>
  );
}

/* ─────────────────────── Tab: Net Profit (last 6 months) ─────────────────────── */
function ProfitTab({ theme, css }) {
  const { user } = useAuth();
  const isRoot = !!user?.role?.is_root;
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const load = async () => {
    try {
      const last6 = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(); d.setMonth(d.getMonth() - i);
        return { y: d.getFullYear(), m: d.getMonth() + 1 };
      });
      const all = await Promise.all(last6.map((p) => api.get(`/finance/dashboard?year=${p.y}&month=${p.m}`).then((r) => ({ ...p, ...r.data }))));
      setRows(all.reverse());
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // STAS-only: erase a polluted month at the source. Bulk-voids every
  // non-reversal ledger entry whose date falls inside the chosen month,
  // appending offsetting reversal rows for each one. Earnings, Net
  // Profit, Overview and Cash all auto-update because they're derived.
  const wipeMonth = async (y, m) => {
    const reason = window.prompt(
      `إبطال كل قيود شهر ${y}-${String(m).padStart(2, "0")}\n\n` +
      `هذا الإجراء يُنشئ قيود عكسية تلقائياً لكل قيود الدخل/المصاريف/الرواتب في هذا الشهر.\n` +
      `صافي الربح وأرباح الموظفين ستُعاد للصفر تلقائياً.\n\n` +
      `اكتب سبب الإبطال (3+ أحرف):`,
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      const { data } = await api.post("/finance/ledger/bulk-void-month", {
        year: y, month: m, reason: reason.trim(),
      });
      alert(`✓ تم إبطال ${data.voided_count} قيد. صافي الربح تحدّث الآن.`);
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  if (err) return <div style={css.err}>{err}</div>;
  return (
    <section style={css.card}>
      <h2 style={css.cardTitle}>صافي الربح — آخر 6 أشهر</h2>
      <Table head={["الشهر", "الدخل", "المصاريف", "الرواتب", "صافي الربح", isRoot ? "إبطال الشهر" : ""].filter(Boolean)}
        rows={rows.map((r) => {
          const row = [
            `${r.y}-${String(r.m).padStart(2, "0")}`,
            <Money amount={r.income?.total || 0} size={12} color="#16a34a" />,
            <Money amount={r.expenses?.total || 0} size={12} color="#dc2626" />,
            <Money amount={r.salaries?.total || 0} size={12} color="#a855f7" />,
            <Money amount={r.net_profit || 0} size={13} bold color={(r.net_profit || 0) >= 0 ? "#16a34a" : "#dc2626"} />,
          ];
          if (isRoot) {
            const hasAny = (r.income?.total || 0) + (r.expenses?.total || 0) + (r.salaries?.total || 0) > 0;
            row.push(
              hasAny ? (
                <IconBtn onClick={() => wipeMonth(r.y, r.m)} title="إبطال جميع قيود الشهر (STAS)" color="#dc2626" testid={`bulk-void-month-${r.y}-${r.m}`}>
                  <Trash2 size={14} />
                </IconBtn>
              ) : <span style={{ fontSize: 10, opacity: 0.4 }}>—</span>,
            );
          }
          return row;
        })} theme={theme} testid="profit-table" />
      {isRoot && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          🛡️ زر سلة المهملات في عمود «إبطال الشهر» يبطل كل قيود ذلك الشهر دفعة واحدة (الدخل + المصاريف + الرواتب) ويُنشئ قيود عكسية في الدفتر. يخصم من أرباح الموظفين تلقائياً. مفيد لتنظيف بيانات تجريبية.
        </div>
      )}
    </section>
  );
}

/* ─────────────────────── Tab: Cash Flow ─────────────────────── */
function CashTab({ theme, css }) {
  const { user } = useAuth();
  const isRoot = !!user?.role?.is_root;
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = () => {
    api.get("/finance/cash-drawer?limit=100").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  // STAS-only: void a single cash drawer movement. The endpoint flags the
  // original as voided AND appends an offsetting movement so the running
  // balance self-corrects without rewriting history. Audit-logged.
  const voidMv = async (mvId, direction, amount, reason) => {
    const why = window.prompt(
      `إبطال حركة كاش (${direction === "in" ? "داخل" : "خارج"} — ${fmtMoney(amount)} ر.س)\n\n` +
      `السبب الحالي: ${reason || "—"}\n\nاكتب سبب الإبطال:`,
    );
    if (!why || why.trim().length < 3) return;
    try {
      await api.post(`/finance/cash-drawer/${mvId}/void`, { reason: why.trim() });
      load();
      alert("✓ تم إبطال الحركة وتعديل الرصيد.");
    } catch (e) { setErr(formatApiError(e)); }
  };

  if (err) return <div style={css.err}>{err}</div>;
  if (!data) return <div style={css.muted}>…</div>;
  return (
    <div>
      <section style={{ ...css.card, ...css.heroCard }}>
        <div style={{ fontSize: 13, color: theme.textMuted }}>رصيد الصندوق الحالي</div>
        <div style={{ marginTop: 6, fontSize: 32, fontWeight: 900, color: theme.gold }}>
          <Money amount={data.balance} size={26} bold color={theme.gold} />
        </div>
      </section>
      <section style={css.card}>
        <h2 style={css.cardTitle}>ملخص يومي (آخر 30 يوم)</h2>
        {(data.daily_summary || []).length === 0 ? <div style={css.muted}>لا توجد حركات كاش بعد.</div> :
          <Table head={["التاريخ", "داخل", "خارج", "الصافي"]}
            rows={data.daily_summary.map((d) => [
              d.date,
              <Money amount={d.in_total} size={12} color="#16a34a" />,
              <Money amount={d.out_total} size={12} color="#dc2626" />,
              <Money amount={d.net} size={12} bold color={d.net >= 0 ? "#16a34a" : "#dc2626"} />,
            ])} theme={theme} testid="cash-daily" />}
      </section>
      <section style={css.card}>
        <h2 style={css.cardTitle}>آخر حركات الصندوق</h2>
        {(data.movements || []).length === 0 ? <div style={css.muted}>—</div> :
          <Table head={["الوقت", "النوع", "المبلغ", "الرصيد بعدها", "السبب", isRoot ? "إجراءات" : ""].filter(Boolean)}
            rows={data.movements.slice(0, 50).map((m) => {
              const row = [
                fmtDateTime(m.date),
                <span style={{ color: m.voided ? "#94a3b8" : (m.direction === "in" ? "#16a34a" : "#dc2626"), fontWeight: 800, textDecoration: m.voided ? "line-through" : "none" }}>
                  {m.direction === "in" ? "داخل" : "خارج"}
                </span>,
                <span style={m.voided ? { textDecoration: "line-through", opacity: 0.55 } : {}}>
                  <Money amount={m.amount} size={12} />
                </span>,
                <Money amount={m.balance_after} size={12} bold />,
                truncate(m.reason, 50),
              ];
              if (isRoot) {
                row.push(
                  !m.voided ? (
                    <IconBtn onClick={() => voidMv(m.id, m.direction, m.amount, m.reason)} title="إبطال (STAS)" color="#dc2626" testid={`force-void-cash-${m.id}`}>
                      <Trash2 size={14} />
                    </IconBtn>
                  ) : <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 700 }}>مُبطَلة</span>,
                );
              }
              return row;
            })} theme={theme} testid="cash-moves" />}
      </section>
    </div>
  );
}

/* ─────────────────────── Tab: Ledger ─────────────────────── */
function LedgerTab({ year, month, theme, css }) {
  const { user } = useAuth();
  const isRoot = !!user?.role?.is_root;
  const [data, setData] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [voiding, setVoiding] = useState(false);
  const [voidErr, setVoidErr] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [showVoidModal, setShowVoidModal] = useState(false);

  const load = () => {
    const q = filter ? `&type=${filter}` : "";
    api.get(`/finance/ledger?year=${year}&month=${month}&limit=300${q}`)
      .then((r) => { setData(r.data); setSelected(new Set()); })
      .catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, filter]);
  const checkIntegrity = async () => {
    try { const r = await api.get("/finance/ledger/integrity"); setIntegrity(r.data); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const toggleOne = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (!data?.items) return;
    const eligibleIds = data.items
      .filter((it) => !it.voided && it.type !== "reversal")
      .map((it) => it.id);
    setSelected((s) => {
      if (s.size === eligibleIds.length) return new Set();
      return new Set(eligibleIds);
    });
  };

  // Pre-compute the financial impact of the current selection so STAS knows
  // exactly what they're about to undo before they confirm.
  const selectionSummary = useMemo(() => {
    if (!data?.items) return { total: 0, count: 0, byUser: {} };
    let total = 0;
    const byUser = {};
    for (const it of data.items) {
      if (!selected.has(it.id)) continue;
      total += Number(it.amount || 0);
      if (it.type === "income" && it.user_id) {
        byUser[it.user_id] = (byUser[it.user_id] || 0) + Number(it.amount || 0);
      }
    }
    return { total: Math.round(total * 100) / 100, count: selected.size, byUser };
  }, [selected, data]);

  const submitVoid = async () => {
    setVoiding(true); setVoidErr("");
    try {
      const r = await api.post("/finance/ledger/bulk-void", {
        entry_ids: Array.from(selected),
        reason: voidReason.trim(),
      });
      await load();
      setShowVoidModal(false);
      setVoidReason("");
      alert(`تم إبطال ${r.data.voided_count} قيد بإجمالي ${fmtMoney(r.data.total_voided_amount)} ر.س`);
    } catch (e) { setVoidErr(formatApiError(e)); }
    finally { setVoiding(false); }
  };

  if (err) return <div style={css.err}>{err}</div>;
  if (!data) return <div style={css.muted}>…</div>;
  return (
    <div>
      <section style={css.card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <h2 style={{ ...css.cardTitle, margin: 0 }}>دفتر القيود ({data.total})</h2>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={inputSt(theme)} data-testid="ledger-filter">
              <option value="">كل الأنواع</option>
              <option value="income">دخل</option>
              <option value="expense">مصروف</option>
              <option value="salary">راتب</option>
              <option value="reversal">قيد عكسي</option>
            </select>
            <button onClick={checkIntegrity} style={btnSecondary(theme)} data-testid="ledger-integrity">
              <ShieldCheck size={14} strokeWidth={2.4} /> فحص السلسلة
            </button>
            {isRoot && selected.size > 0 && (
              <button onClick={() => setShowVoidModal(true)}
                style={{ ...btnGold(theme), background: "#dc2626", color: "#fff" }}
                data-testid="ledger-bulk-void-open">
                <Trash2 size={14} strokeWidth={2.4} /> إبطال المحدد ({selected.size})
              </button>
            )}
          </div>
        </div>
        {integrity && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 10,
            background: integrity.ok ? "rgba(22,163,74,0.10)" : "rgba(220,38,38,0.10)",
            color: integrity.ok ? "#16a34a" : "#dc2626", fontWeight: 800, fontSize: 13 }}
            data-testid="ledger-integrity-result">
            {integrity.ok ? `✓ السلسلة سليمة — تم فحص ${fmtNum(integrity.checked)} قيد` : `✗ السلسلة مكسورة عند القيد ${integrity.first_broken_id}`}
          </div>
        )}
        {(data.items || []).length === 0 ? <div style={css.muted}>لا قيود في هذا الشهر.</div> : (
          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: theme.surface }}>
                  {isRoot && (
                    <th style={{ padding: 8, textAlign: "center", borderBottom: `1px solid ${theme.border}` }}>
                      <input type="checkbox"
                        checked={selected.size > 0 && data.items
                          .filter((it) => !it.voided && it.type !== "reversal").length === selected.size}
                        onChange={toggleAll}
                        data-testid="ledger-select-all" />
                    </th>
                  )}
                  <th style={thSt(theme)}>التاريخ</th>
                  <th style={thSt(theme)}>النوع</th>
                  <th style={thSt(theme)}>الوصف</th>
                  <th style={thSt(theme)}>المبلغ</th>
                  <th style={thSt(theme)}>الحالة</th>
                  <th style={thSt(theme)}>Hash</th>
                  {isRoot && <th style={thSt(theme)}>إجراء</th>}
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => {
                  const isVoided = !!it.voided;
                  const isReversal = it.type === "reversal";
                  const canSelect = isRoot && !isVoided && !isReversal;
                  // Single-row force-void: same endpoint as bulk-void,
                  // passing one id. Lets STAS kill a stray entry without
                  // clicking the checkbox-then-modal dance.
                  const forceVoidOne = async () => {
                    const reason = window.prompt(
                      `إبطال قيد منفرد (${ledgerLabel(it.type)} — ${fmtMoney(it.amount)} ر.س)\n\n` +
                      `سيُنشأ قيد عكسي تلقائياً + خصم من أرباح الموظف إن كان دخلاً.\n\nاكتب السبب:`,
                    );
                    if (!reason || reason.trim().length < 3) return;
                    try {
                      await api.post("/finance/ledger/bulk-void", { entry_ids: [it.id], reason: reason.trim() });
                      load();
                    } catch (e) { alert(formatApiError(e)); }
                  };
                  return (
                    <tr key={it.id} style={{
                      borderBottom: `1px solid ${theme.borderSoft}`,
                      opacity: isVoided ? 0.5 : 1,
                      background: selected.has(it.id) ? "rgba(220,38,38,0.06)" : "transparent",
                      textDecoration: isVoided ? "line-through" : "none",
                    }} data-testid={`ledger-row-${it.id}`}>
                      {isRoot && (
                        <td style={{ padding: 8, textAlign: "center" }}>
                          {canSelect && (
                            <input type="checkbox"
                              checked={selected.has(it.id)}
                              onChange={() => toggleOne(it.id)}
                              data-testid={`ledger-select-${it.id}`} />
                          )}
                        </td>
                      )}
                      <td style={tdSt(theme)}>{fmtDateTime(it.date)}</td>
                      <td style={{ ...tdSt(theme), color: ledgerColor(it.type), fontWeight: 800 }}>{ledgerLabel(it.type)}</td>
                      <td style={tdSt(theme)}>{truncate(it.description, 60)}</td>
                      <td style={tdSt(theme)}>
                        <Money amount={it.amount} size={12} bold color={ledgerColor(it.type)} />
                      </td>
                      <td style={tdSt(theme)}>
                        {isVoided ? (
                          <span style={{
                            padding: "2px 8px", borderRadius: 999, background: "rgba(220,38,38,0.15)",
                            color: "#dc2626", fontWeight: 800, fontSize: 10,
                          }} data-testid={`ledger-voided-${it.id}`}>
                            مُبطَل
                          </span>
                        ) : "—"}
                      </td>
                      <td style={tdSt(theme)}>
                        <code style={{ fontSize: 10, color: theme.textMuted }}>{(it.hash || "").slice(0, 10)}…</code>
                      </td>
                      {isRoot && (
                        <td style={tdSt(theme)}>
                          {canSelect ? (
                            <IconBtn onClick={forceVoidOne} title="إبطال" color="#dc2626" testid={`ledger-force-void-${it.id}`}>
                              <Trash2 size={14} />
                            </IconBtn>
                          ) : <span style={{ fontSize: 10, opacity: 0.5 }}>—</span>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showVoidModal && (
        <BulkVoidConfirmModal
          summary={selectionSummary}
          reason={voidReason}
          setReason={setVoidReason}
          onClose={() => { setShowVoidModal(false); setVoidErr(""); }}
          onConfirm={submitVoid}
          busy={voiding}
          err={voidErr}
          theme={theme}
        />
      )}
    </div>
  );
}

function thSt(theme) {
  return { padding: 8, textAlign: "right", fontSize: 11, fontWeight: 900,
    color: theme.textMuted, borderBottom: `1px solid ${theme.border}` };
}
function tdSt(theme) {
  return { padding: 8, textAlign: "right", color: theme.text };
}

function BulkVoidConfirmModal({ summary, reason, setReason, onClose, onConfirm, busy, err, theme }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: theme.surface, borderRadius: 14, padding: 20, maxWidth: 480, width: "92%",
        border: `2px solid #dc2626`,
      }} data-testid="ledger-bulk-void-modal">
        <h3 style={{ margin: "0 0 12px", color: "#dc2626", fontSize: 16, fontWeight: 900 }}>
          ⚠️ إبطال {summary.count} قيد مالي
        </h3>
        <div style={{
          padding: 12, borderRadius: 10, background: "rgba(220,38,38,0.06)",
          border: "1px dashed #dc2626", marginBottom: 14, fontSize: 12, lineHeight: 1.8,
        }}>
          <div><strong>إجمالي المبلغ:</strong> {fmtMoney(summary.total)} ر.س</div>
          {Object.keys(summary.byUser).length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>الخصم من أرباح الموظفين:</strong>
              <ul style={{ margin: "4px 0 0", paddingInlineStart: 20 }}>
                {Object.entries(summary.byUser).map(([uid, amt]) => (
                  <li key={uid}>{uid.slice(0, 8)}… ← {fmtMoney(amt)} ر.س</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: theme.textMuted }}>
            سيتم إنشاء قيد عكسي لكل بند (للحفاظ على سلسلة التدقيق) + تعديل أرباح الموظفين تلقائياً.
          </div>
        </div>
        <label style={{ display: "block", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
          سبب الإبطال (إلزامي):
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثال: معاملات تجريبية تم إدخالها بالخطأ من قبل الموظف…"
          style={{
            width: "100%", minHeight: 80, padding: 10, borderRadius: 8,
            border: `1px solid ${theme.border}`, background: theme.bg,
            color: theme.text, fontSize: 12, fontFamily: "inherit", resize: "vertical",
            boxSizing: "border-box",
          }}
          data-testid="ledger-void-reason"
        />
        {err && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 8 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={busy}
            style={{ ...btnSecondary(theme), padding: "10px 16px" }}>
            إلغاء
          </button>
          <button onClick={onConfirm} disabled={busy || reason.trim().length < 3}
            style={{ ...btnGold(theme), background: "#dc2626", color: "#fff", padding: "10px 16px" }}
            data-testid="ledger-void-confirm">
            {busy ? "...جاري" : "تأكيد الإبطال"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Tab: Period Locks (STAS) ─────────────────────── */
function LocksTab({ theme, css }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [target, setTarget] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });

  const load = async () => {
    try { const r = await api.get("/finance/period-locks"); setItems(r.data || []); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const lock = async () => {
    if (!window.confirm(`قفل الشهر ${target.year}-${String(target.month).padStart(2, "0")} مالياً؟ لن يمكن إضافة قيود حتى يفتحه STAS مرة أخرى.`)) return;
    setBusy(true);
    try { await api.post("/finance/period-locks", target); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const unlock = async (y, m) => {
    if (!window.confirm(`فتح الشهر ${y}-${String(m).padStart(2, "0")}؟`)) return;
    setBusy(true);
    try { await api.post("/finance/period-locks/unlock", { year: y, month: m }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section style={css.card}>
        <div style={{ ...css.warningBox, marginBottom: 12 }}>
          <AlertTriangle size={14} /> قفل الشهر يجعل القيود **غير قابلة للتعديل** — استخدمها بعد التأكد من تسجيل كل المعاملات.
        </div>
        <h2 style={css.cardTitle}>قفل فترة جديدة</h2>
        <div style={css.formRow}>
          <select value={target.month} onChange={(e) => setTarget({ ...target, month: parseInt(e.target.value, 10) })} style={inputSt(theme)} data-testid="lock-month">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((mm) => <option key={mm} value={mm}>{String(mm).padStart(2, "0")}</option>)}
          </select>
          <select value={target.year} onChange={(e) => setTarget({ ...target, year: parseInt(e.target.value, 10) })} style={inputSt(theme)} data-testid="lock-year">
            {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i).map((yy) => <option key={yy} value={yy}>{yy}</option>)}
          </select>
          <button onClick={lock} disabled={busy} style={{ ...btnGold(theme), background: "#dc2626" }} data-testid="lock-btn">
            <Lock size={14} strokeWidth={2.4} /> قفل الشهر
          </button>
        </div>
      </section>

      {err && <div style={css.err}>{err}</div>}

      <section style={css.card}>
        <h2 style={css.cardTitle}>سجل القفل ({items.length})</h2>
        {items.length === 0 ? <div style={css.muted}>لا يوجد فترات مقفلة.</div> :
          <Table head={["الشهر", "تاريخ القفل", "تاريخ الفتح", "إجراء"]}
            rows={items.map((it) => [
              `${it.year}-${String(it.month).padStart(2, "0")}`,
              fmtDateTime(it.locked_at),
              it.unlocked_at ? fmtDateTime(it.unlocked_at) : "—",
              !it.unlocked_at && (
                <IconBtn onClick={() => unlock(it.year, it.month)} title="فتح" color="#16a34a" testid={`unlock-${it.year}-${it.month}`}><Unlock size={14} /></IconBtn>
              ),
            ])} theme={theme} testid="locks-table" />}
      </section>
    </div>
  );
}

/* ─────────────────────── Tiny shared UI ─────────────────────── */
function Table({ head, rows, theme, testid }) {
  return (
    <div style={{ overflowX: "auto" }} data-testid={testid}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>{head.map((h, i) => (
            <th key={i} style={{ textAlign: "start", padding: "10px 12px", color: theme.textMuted, fontWeight: 700, borderBottom: `1px solid ${theme.border}` }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${theme.borderSoft}` }}>
              {r.map((c, j) => <td key={j} style={{ padding: "10px 12px" }}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { label: "مسودة", color: "#f59e0b" },
    approved: { label: "معتمد", color: "#16a34a" },
    rejected: { label: "مرفوض", color: "#dc2626" },
    paid: { label: "مصروف", color: "#16a34a" },
    voided: { label: "مُبطَل", color: "#dc2626" },
  };
  const s = map[status] || { label: status, color: "#64748b" };
  return <span style={{ background: s.color + "22", color: s.color, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 800 }}>{s.label}</span>;
}

function IconBtn({ onClick, color, title, children, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      style={{ background: color + "20", border: "none", color, padding: 6, borderRadius: 8, cursor: "pointer", display: "inline-flex" }}>
      {children}
    </button>
  );
}

function truncate(s, n) { if (!s) return "—"; return s.length > n ? s.slice(0, n - 1) + "…" : s; }
function methodLabel(m) {
  return ({ cash: "كاش", mada: "مدى", visa_network: "فيزا", bank_transfer: "تحويل بنكي" })[m] || m || "—";
}
function ledgerLabel(t) {
  return ({ income: "دخل", expense: "مصروف", salary: "راتب", reversal: "عكسي",
            cash_in: "كاش داخل", cash_out: "كاش خارج", adjustment: "تسوية" })[t] || t;
}
function ledgerColor(t) {
  return ({ income: "#16a34a", expense: "#dc2626", salary: "#a855f7", reversal: "#f59e0b" })[t] || "#64748b";
}

/* ─────────────────────── Styles ─────────────────────── */
const inputSt = (theme) => ({
  background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`,
  borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", outline: "none",
});
const btnGold = (theme) => ({
  background: theme.gold, color: "#0a0a0a", border: "none", padding: "8px 14px",
  borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
});
const btnSecondary = (theme) => ({
  background: "transparent", color: theme.text, border: `1px solid ${theme.border}`,
  padding: "8px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
});
const kpiCardSt = (theme) => ({
  background: theme.surface, border: `1px solid ${theme.border}`,
  borderRadius: 14, padding: 16, minHeight: 96, display: "flex", flexDirection: "column",
});
function makeStyles(theme) {
  return {
    page: { padding: 18, color: theme.text, background: theme.bg, minHeight: "100vh" },
    head: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 18 },
    title: { margin: 0, fontSize: 22, fontWeight: 900 },
    sub: { margin: 0, color: theme.textMuted, fontSize: 12 },
    backBtn: { background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
               padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
               display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" },
    tabsBar: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, paddingBottom: 8,
               borderBottom: `1px solid ${theme.border}` },
    tab: { background: "transparent", border: `1px solid ${theme.border}`, color: theme.textSoft,
           padding: "8px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700,
           display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" },
    tabActive: { background: theme.gold, color: "#0a0a0a", borderColor: theme.gold },
    body: { display: "flex", flexDirection: "column", gap: 12 },
    card: { background: theme.surface, border: `1px solid ${theme.border}`,
            borderRadius: 14, padding: 16 },
    cardTitle: { margin: "0 0 12px 0", fontSize: 14, fontWeight: 900 },
    heroCard: { background: `linear-gradient(135deg, ${theme.surface}, ${theme.bg})`,
                borderColor: theme.gold + "55" },
    formRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
    kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
               gap: 10, marginBottom: 14 },
    kpiCard: kpiCardSt(theme),
    kpiLabel: { fontSize: 12, color: theme.textMuted, fontWeight: 700 },
    kpiValue: { marginTop: 8, fontSize: 20, fontWeight: 900, color: theme.text },
    err: { color: "#dc2626", padding: 12, background: "rgba(220,38,38,0.10)", borderRadius: 10, fontSize: 13, fontWeight: 700 },
    muted: { color: theme.textMuted, padding: 8, fontSize: 13 },
    warningBox: { background: "rgba(245,158,11,0.12)", color: "#f59e0b", padding: "10px 12px",
                  borderRadius: 10, fontSize: 12, fontWeight: 700,
                  display: "flex", alignItems: "center", gap: 8 },
  };
}

/* ═══════════════════════════════════════════════════════════════
   Tab: Partners — creditor/debtor subledger per partner
   ═══════════════════════════════════════════════════════════════ */
function PartnersTab({ theme, css, canWrite }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  // New-partner form state
  const [nUserId, setNUserId] = useState("");
  const [nDisplay, setNDisplay] = useState("");
  const [nRole, setNRole] = useState("");
  const [nOpening, setNOpening] = useState("");
  const [nNotes, setNNotes] = useState("");

  const load = async () => {
    try {
      setErr("");
      const [p, u] = await Promise.all([
        api.get("/finance/partners"),
        api.get("/stas-users").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(p.data?.items || []);
      // Extract available users (not-yet-linked)
      const raw = u.data?.items || u.data?.users || u.data || [];
      setUsers(Array.isArray(raw) ? raw : []);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const linkedUserIds = new Set(items.map((i) => i.user_id).filter(Boolean));
  const availableUsers = users.filter((u) => !linkedUserIds.has(u.id || u._id));

  const createPartner = async () => {
    setErr("");
    if (!nUserId && !nDisplay.trim()) { setErr("اختر مستخدم أو اكتب اسم الشريك"); return; }
    setBusy(true);
    try {
      await api.post("/finance/partners", {
        user_id: nUserId || null,
        display_name: nDisplay.trim() || null,
        role_label: nRole.trim() || null,
        opening_balance: Number(nOpening || 0),
        notes: nNotes.trim() || null,
      });
      setShowNew(false);
      setNUserId(""); setNDisplay(""); setNRole(""); setNOpening(""); setNNotes("");
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (err && items.length === 0) return <div style={css.err}>{err}</div>;

  return (
    <div data-testid="finance-partners-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 14, color: theme.textMuted }}>
          إجمالي الشركاء: <strong style={{ color: theme.text }}>{fmtNum(items.length)}</strong>
        </div>
        {canWrite && (
          <button onClick={() => setShowNew((v) => !v)} style={btnGold(theme)} data-testid="partners-new-btn">
            <Plus size={14} /> شريك جديد
          </button>
        )}
      </div>

      {showNew && (
        <div style={{ ...css.formRow, padding: 12, borderRadius: 10,
          border: `1px dashed ${theme.gold}`, background: "rgba(212,175,55,0.05)", marginBottom: 12 }}>
          <select value={nUserId} onChange={(e) => setNUserId(e.target.value)}
            style={inputSt(theme)} data-testid="partners-new-user">
            <option value="">— بدون ربط بمستخدم —</option>
            {availableUsers.map((u) => (
              <option key={u.id || u._id} value={u.id || u._id}>
                {u.full_name || u.username} ({u.username})
              </option>
            ))}
          </select>
          <input value={nDisplay} onChange={(e) => setNDisplay(e.target.value)}
            placeholder="اسم للعرض (لو بدون مستخدم)" style={inputSt(theme)} data-testid="partners-new-name" />
          <input value={nRole} onChange={(e) => setNRole(e.target.value)}
            placeholder="الوصف (مدير عام…)" style={inputSt(theme)} data-testid="partners-new-role" />
          <input type="number" value={nOpening} onChange={(e) => setNOpening(e.target.value)}
            placeholder="رصيد افتتاحي (+ مدين / − دائن)" style={inputSt(theme)} data-testid="partners-new-opening" />
          <input value={nNotes} onChange={(e) => setNNotes(e.target.value)}
            placeholder="ملاحظات" style={{ ...inputSt(theme), flex: "1 1 200px", minWidth: 180 }} />
          <button onClick={createPartner} disabled={busy} style={btnGold(theme)} data-testid="partners-new-save">
            حفظ
          </button>
        </div>
      )}
      {err && <div style={{ ...css.err, marginBottom: 8 }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
        {items.length === 0 && (
          <div style={css.muted}>لا يوجد شركاء بعد. أضف شريكاً لبدء تسجيل الدائن والمدين.</div>
        )}
        {items.map((p) => {
          const bal = Number(p.current_balance || 0);
          const isDebtor = bal > 0;
          const isCreditor = bal < 0;
          const color = isDebtor ? "#dc2626" : isCreditor ? "#16a34a" : theme.textMuted;
          const stateLabel = isDebtor ? "مدين للمكتب" : isCreditor ? "دائن على المكتب" : "متساوٍ";
          return (
            <button key={p.id} onClick={() => setSelectedId(p.id)}
              style={{
                textAlign: "start", padding: 14, borderRadius: 12,
                background: theme.surface, border: `1.5px solid ${color}55`,
                cursor: "pointer", color: theme.text, fontFamily: "inherit",
              }}
              data-testid={`partner-card-${p.id}`}
            >
              <div style={{ fontSize: 15, fontWeight: 900, color: theme.text, marginBottom: 4 }}>
                {p.display_name}
              </div>
              {p.role_label && <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8 }}>{p.role_label}</div>}
              <div style={{ fontSize: 22, fontWeight: 900, color, fontFamily: "monospace" }}>
                {fmtNum(Math.abs(bal))} <RiyalSymbol size={14} />
              </div>
              <div style={{ marginTop: 4, fontSize: 12, fontWeight: 800, color }}>{stateLabel}</div>
            </button>
          );
        })}
      </div>

      {selectedId && (
        <PartnerLedgerDrawer
          partnerId={selectedId}
          theme={theme}
          canWrite={canWrite}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function PartnerLedgerDrawer({ partnerId, theme, canWrite, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState("debit");
  const [notes, setNotes] = useState("");

  const load = async () => {
    try {
      setErr("");
      const r = await api.get(`/finance/partners/${partnerId}/ledger`);
      setData(r.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [partnerId]);

  const addEntry = async () => {
    if (!amount || Number(amount) <= 0) { setErr("اكتب مبلغ صحيح"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/finance/partners/${partnerId}/ledger`, {
        amount: Number(amount), direction, notes: notes.trim() || null,
      });
      setAmount(""); setNotes(""); setDirection("debit");
      await load();
      onChanged && onChanged();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const voidEntry = async (entryId) => {
    if (!window.confirm("إلغاء هذا القيد نهائياً؟")) return;
    setBusy(true);
    try {
      await api.delete(`/finance/partners/${partnerId}/ledger/${entryId}`);
      await load();
      onChanged && onChanged();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto",
      padding: 20,
    }} data-testid="partner-drawer">
      <div onClick={(e) => e.stopPropagation()} style={{
        background: theme.bg, color: theme.text, borderRadius: 14, padding: 20,
        maxWidth: 900, width: "100%", border: `1px solid ${theme.border}`,
        boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>
            {data?.partner?.display_name || "…"}
            {data?.partner?.role_label && <span style={{ fontSize: 12, color: theme.textMuted, marginInlineStart: 8, fontWeight: 600 }}>({data.partner.role_label})</span>}
          </h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: theme.text, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        {data?.partner && (
          <div style={{ padding: 14, borderRadius: 10, background: theme.surface, marginBottom: 14,
            display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 700 }}>الرصيد الحالي</div>
              <div style={{ fontSize: 26, fontWeight: 900, fontFamily: "monospace",
                color: data.partner.current_balance > 0 ? "#dc2626" : data.partner.current_balance < 0 ? "#16a34a" : theme.text }}>
                {fmtNum(Math.abs(data.partner.current_balance))} <RiyalSymbol size={16} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 800,
                color: data.partner.current_balance > 0 ? "#dc2626" : "#16a34a" }}>
                {data.partner.current_balance > 0 ? "مدين للمكتب" : data.partner.current_balance < 0 ? "دائن على المكتب" : "متساوٍ"}
              </div>
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted }}>
              رصيد افتتاحي: <strong style={{ color: theme.text }}>{fmtNum(data.partner.opening_balance)} <RiyalSymbol size={11} /></strong>
            </div>
          </div>
        )}

        {canWrite && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, padding: 12,
            borderRadius: 10, background: "rgba(212,175,55,0.06)", border: `1px dashed ${theme.gold}` }}>
            <select value={direction} onChange={(e) => setDirection(e.target.value)}
              style={inputSt(theme)} data-testid="partner-entry-direction">
              <option value="debit">➕ مدين (زيادة الدَّين)</option>
              <option value="credit">➖ دائن (تسديد/خصم)</option>
            </select>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="المبلغ" style={{ ...inputSt(theme), maxWidth: 140 }} data-testid="partner-entry-amount" />
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="ملاحظة (سبب القيد)" style={{ ...inputSt(theme), flex: "1 1 200px", minWidth: 180 }}
              data-testid="partner-entry-notes" />
            <button onClick={addEntry} disabled={busy} style={btnGold(theme)} data-testid="partner-entry-save">
              إضافة
            </button>
          </div>
        )}
        {err && <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 10 }}>{err}</div>}

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: theme.surface, color: theme.textMuted, fontSize: 11 }}>
              <th style={{ padding: 8, textAlign: "start" }}>التاريخ</th>
              <th style={{ padding: 8, textAlign: "start" }}>القيد</th>
              <th style={{ padding: 8, textAlign: "end" }}>مدين</th>
              <th style={{ padding: 8, textAlign: "end" }}>دائن</th>
              <th style={{ padding: 8, textAlign: "end" }}>الرصيد</th>
              <th style={{ padding: 8 }}></th>
            </tr>
          </thead>
          <tbody>
            {(data?.entries || []).map((e) => {
              const isDebit = e.direction === "debit";
              return (
                <tr key={e.id} style={{ borderBottom: `1px solid ${theme.border}`, opacity: e.voided ? 0.4 : 1 }}
                  data-testid={`partner-entry-row-${e.id}`}>
                  <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{e.entry_date}</td>
                  <td style={{ padding: 8 }}>
                    {e.voided && <span style={{ color: "#dc2626", fontSize: 10, fontWeight: 800, marginInlineEnd: 4 }}>[ملغى]</span>}
                    {e.source_kind === "manual"
                      ? <span style={{ color: theme.gold, fontSize: 10, fontWeight: 700 }}>يدوي • </span>
                      : <span style={{ color: "#16a34a", fontSize: 10, fontWeight: 700 }}>تلقائي ({e.source_kind}) • </span>}
                    {e.notes}
                  </td>
                  <td style={{ padding: 8, textAlign: "end", fontFamily: "monospace",
                    color: isDebit ? "#dc2626" : "transparent", fontWeight: 700 }}>
                    {isDebit ? fmtNum(e.amount) : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "end", fontFamily: "monospace",
                    color: !isDebit ? "#16a34a" : "transparent", fontWeight: 700 }}>
                    {!isDebit ? fmtNum(e.amount) : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "end", fontFamily: "monospace", fontWeight: 800 }}>
                    {fmtNum(e.running_balance)}
                  </td>
                  <td style={{ padding: 8, textAlign: "center" }}>
                    {canWrite && !e.voided && e.source_kind === "manual" && (
                      <button onClick={() => voidEntry(e.id)}
                        style={{ background: "transparent", border: "none", color: "#dc2626", cursor: "pointer" }}
                        data-testid={`partner-entry-void-${e.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {(data?.entries || []).length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: theme.textMuted }}>
                لا يوجد قيود بعد.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Tab: Partnership Settings — salary caps + profit shares
   propose → STAS approves workflow
   ═══════════════════════════════════════════════════════════════ */
function PartnershipTab({ theme, css, isRoot, canWrite }) {
  const [state, setState] = useState({ active: null, pending: null });
  const [partners, setPartners] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [caps, setCaps] = useState({}); // partner_id -> monthly_cap
  const [shares, setShares] = useState({}); // partner_id -> percent
  const [notes, setNotes] = useState("");

  const load = async () => {
    try {
      setErr("");
      const [s, p] = await Promise.all([
        api.get("/finance/partnership-settings"),
        api.get("/finance/partners"),
      ]);
      setState(s.data || { active: null, pending: null });
      setPartners(p.data?.items || []);
      // Prefill from active settings so accountant edits from current baseline
      const source = s.data?.pending || s.data?.active;
      if (source) {
        const cMap = {};
        (source.salary_caps || []).forEach((c) => { cMap[c.partner_id] = c.monthly_cap; });
        setCaps(cMap);
        const shMap = {};
        (source.profit_shares || []).forEach((sh) => { shMap[sh.partner_id] = sh.percent; });
        setShares(shMap);
      }
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submitProposal = async () => {
    // Only include rows with non-zero caps/shares to keep the proposal tight
    const salary_caps = Object.entries(caps)
      .filter(([, v]) => v !== "" && v !== null && Number(v) >= 0)
      .map(([partner_id, monthly_cap]) => ({ partner_id, monthly_cap: Number(monthly_cap) }));
    const profit_shares = Object.entries(shares)
      .filter(([, v]) => v !== "" && v !== null && Number(v) > 0)
      .map(([partner_id, percent]) => ({ partner_id, percent: Number(percent) }));
    const totalShare = profit_shares.reduce((s, x) => s + x.percent, 0);
    if (profit_shares.length > 0 && Math.abs(totalShare - 100) > 0.01) {
      setErr(`مجموع نسب الشراكة يجب أن يساوي 100% (المجموع الحالي: ${totalShare}%)`);
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post("/finance/partnership-settings/propose", {
        salary_caps, profit_shares, notes: notes.trim() || null,
      });
      setNotes("");
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const approveProposal = async () => {
    if (!state.pending) return;
    if (!window.confirm("اعتماد هذا الاقتراح كسياسة نشطة؟")) return;
    setBusy(true);
    try {
      await api.post(`/finance/partnership-settings/approve/${state.pending.id}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const rejectProposal = async () => {
    if (!state.pending) return;
    if (!window.confirm("رفض هذا الاقتراح؟")) return;
    setBusy(true);
    try {
      await api.post(`/finance/partnership-settings/reject/${state.pending.id}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const totalShare = Object.values(shares).reduce((s, v) => s + (Number(v) || 0), 0);

  return (
    <div data-testid="finance-partnership-tab">
      {state.pending && (
        <div style={{
          padding: 14, borderRadius: 10, marginBottom: 14,
          background: "rgba(245,158,11,0.12)", border: "1.5px solid #f59e0b",
        }} data-testid="partnership-pending-banner">
          <div style={{ fontSize: 14, fontWeight: 900, color: "#b45309", marginBottom: 8 }}>
            🕓 يوجد اقتراح معلّق بانتظار موافقة STAS
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10 }}>
            {state.pending.notes || "بدون ملاحظات"}
          </div>
          {isRoot ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={approveProposal} disabled={busy} style={btnGold(theme)} data-testid="partnership-approve">
                <CheckCircle2 size={14} /> اعتماد
              </button>
              <button onClick={rejectProposal} disabled={busy}
                style={{ ...btnGold(theme), background: "#dc2626", color: "#fff" }} data-testid="partnership-reject">
                <XCircle size={14} /> رفض
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: theme.textMuted }}>الاعتماد يتم عبر STAS فقط.</div>
          )}
        </div>
      )}

      {state.active && (
        <div style={{ padding: 12, borderRadius: 10, marginBottom: 14,
          background: "rgba(22,163,74,0.10)", border: "1px solid #16a34a" }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: "#16a34a" }}>
            ● السياسة النشطة (معتمدة {state.active.approved_at ? fmtDate(state.active.approved_at) : ""})
          </div>
        </div>
      )}

      <div style={{ fontSize: 15, fontWeight: 900, color: theme.text, marginBottom: 8 }}>
        سقوف الرواتب الشهرية (ما يزيد يُوزَّع كأرباح شراكة)
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20, fontSize: 13 }}>
        <thead>
          <tr style={{ color: theme.textMuted, fontSize: 11 }}>
            <th style={{ padding: 8, textAlign: "start" }}>الشريك</th>
            <th style={{ padding: 8, textAlign: "end" }}>السقف الشهري (ر.س)</th>
          </tr>
        </thead>
        <tbody>
          {partners.map((p) => (
            <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <td style={{ padding: 8 }}>{p.display_name}
                {p.role_label && <span style={{ color: theme.textMuted, fontSize: 11, marginInlineStart: 6 }}>({p.role_label})</span>}
              </td>
              <td style={{ padding: 8, textAlign: "end" }}>
                <input type="number" min="0"
                  value={caps[p.id] ?? ""}
                  onChange={(e) => setCaps({ ...caps, [p.id]: e.target.value })}
                  disabled={!canWrite}
                  placeholder="0"
                  style={{ ...inputSt(theme), width: 140, textAlign: "end", fontFamily: "monospace" }}
                  data-testid={`partnership-cap-${p.id}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ fontSize: 15, fontWeight: 900, color: theme.text, marginBottom: 8 }}>
        نسب توزيع الأرباح (%)
        <span style={{ fontSize: 12, color: totalShare === 100 ? "#16a34a" : "#dc2626",
          fontWeight: 700, marginInlineStart: 10 }}>
          المجموع: {totalShare}%
        </span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14, fontSize: 13 }}>
        <thead>
          <tr style={{ color: theme.textMuted, fontSize: 11 }}>
            <th style={{ padding: 8, textAlign: "start" }}>الشريك</th>
            <th style={{ padding: 8, textAlign: "end" }}>النسبة %</th>
          </tr>
        </thead>
        <tbody>
          {partners.map((p) => (
            <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <td style={{ padding: 8 }}>{p.display_name}</td>
              <td style={{ padding: 8, textAlign: "end" }}>
                <input type="number" min="0" max="100"
                  value={shares[p.id] ?? ""}
                  onChange={(e) => setShares({ ...shares, [p.id]: e.target.value })}
                  disabled={!canWrite}
                  placeholder="0"
                  style={{ ...inputSt(theme), width: 100, textAlign: "end", fontFamily: "monospace" }}
                  data-testid={`partnership-share-${p.id}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {canWrite && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="ملاحظات المحاسبة (سبب الاقتراح، السياق…)"
            style={{ ...inputSt(theme), flex: "1 1 300px", minWidth: 240, fontFamily: "inherit", resize: "vertical" }}
            data-testid="partnership-notes" />
          <button onClick={submitProposal} disabled={busy} style={btnGold(theme)} data-testid="partnership-propose">
            {state.pending ? "استبدال المقترح المعلّق" : "إرسال المقترح لـSTAS"}
          </button>
        </div>
      )}
      {err && <div style={{ ...css.err, marginTop: 10 }}>{err}</div>}
    </div>
  );
}

function btnGoldSm(theme) {
  return {
    padding: "8px 14px", borderRadius: 8, border: "none",
    background: theme.goldGradient || theme.gold, color: "#0A0F19",
    fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
    display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36,
  };
}
